import { getTranslations } from 'next-intl/server';
import clsx from 'clsx';
import { envConfig, featureFlipping } from '@headless-workspace/config';
import { UIFooterLinkListItems, UIFooterShell, UIThemeSwitcher } from '@headless-workspace/core-ui';
import {
    GlowClickable,
    GlowDivider,
    GlowIcon,
    GlowReinsuranceListItem,
    GlowTextBody,
    GlowTextBodySmall,
    Icons,
    PropsWithLocale,
} from '@headless-workspace/glow-ds';
import { AsyncComponent, CountriesLink, FooterLegalLinks } from '../../../../src';
import { InnerHTML } from '../../../client';
import { getMemoFooterSlots } from '../preload/getMemoFooterSlots';
import { FooterAdditionalMentions } from './FooterAdditionalMentions';

type FooterProps = PropsWithLocale;

export const Footer: AsyncComponent<FooterProps> = async ({ locale }) => {
    const t = await getTranslations('Footer');
    const footerInformationResult = await getMemoFooterSlots(locale);

    return (
        footerInformationResult.type === 'success' && (
            <UIFooterShell>
                <div className={'bg-background-l0'}>
                    <div className={clsx('px-1 py-1.5 desktop:!p-1.5', 'desktop:mx-auto desktop:w-maxWidth')}>
                        <ul
                            className={clsx(
                                'grid grid-cols-1 tablet:grid-cols-2 desktopS:grid-cols-2 desktop:grid-cols-4',
                                'gap-1.5',
                            )}
                        >
                            {footerInformationResult.data.reinsurance.map((item, index) => (
                                <GlowReinsuranceListItem
                                    key={item.id}
                                    image={item.image}
                                    title={item.title}
                                    description={item.description}
                                    cta={item.cta}
                                    indexItem={index}
                                />
                            ))}
                        </ul>
                    </div>
                </div>
                <div className={'bg-background-brand'}>
                    <div
                        className={clsx(
                            'flex flex-col gap-1.5',
                            'px-1 pb-1.5 desktop:px-1.5 desktop:pt-1.5',
                            'bg-background-brand',
                            'desktop:mx-auto desktop:w-maxWidth',
                        )}
                    >
                        <ul className={'flex flex-col desktop:flex-row desktop:gap-1.5 pt-1.5'}>
                            <UIFooterLinkListItems links={footerInformationResult.data.links} />
                            <li className={'flex flex-col justify-between gap-3 pt-1.5 desktop:pt-0'}>
                                <div className={'flex flex-col gap-0.5 desktop:gap-1'}>
                                    <GlowTextBody
                                        text={t('links.label.followUs')}
                                        fontWeight={'bold'}
                                        color={'primary-inverse-fixed'}
                                        className={'text-wrap'}
                                    />
                                    <ul className={'flex flex-row gap-1.5'}>
                                        {footerInformationResult.data.socialNetwork.map((socialNetwork) => (
                                            <li key={socialNetwork.id}>
                                                <GlowClickable
                                                    href={socialNetwork.href}
                                                    content={{
                                                        icon: <InnerHTML rawHtml={socialNetwork.iconRaw} />,
                                                    }}
                                                />
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                                <div className={'flex flex-col gap-0.5'}>
                                    <GlowTextBody
                                        text={t('links.label.downloadApp')}
                                        color={'primary-inverse-fixed'}
                                        className={'text-wrap'}
                                    />
                                    <div className={'flex flex-row gap-0.5'}>
                                        <GlowClickable
                                            ariaLabel={t('links.ariaLabel.googlePlay')}
                                            href={t('appStore.externalLink.googlePlayStore')}
                                            content={{
                                                icon: (
                                                    <GlowIcon
                                                        Icon={Icons.DownloadAppFromGooglePlay}
                                                        className={'h-appDownloadIcon'}
                                                        type={'fullSize'}
                                                    />
                                                ),
                                            }}
                                        />
                                        <GlowClickable
                                            ariaLabel={t('links.ariaLabel.appStore')}
                                            href={t('appStore.externalLink.appleAppStore')}
                                            content={{
                                                icon: (
                                                    <GlowIcon
                                                        Icon={Icons.DownloadAppFromAppStore}
                                                        className={'h-appDownloadIcon'}
                                                        type={'fullSize'}
                                                    />
                                                ),
                                            }}
                                        />
                                    </div>
                                </div>
                            </li>
                        </ul>
                        {featureFlipping.isThemeSwitcherEnabled && <UIThemeSwitcher />}
                        <FooterAdditionalMentions />
                    </div>

                    <div className={'bg-background-brand px-1 desktop:px-0'}>
                        <GlowDivider />
                    </div>
                    <div
                        className={clsx(
                            'flex flex-col desktop:flex-row justify-between desktop:gap-2.5',
                            'pr-5 pl-1 py-1.5 desktop:pl-1.5',
                            'bg-background-brand',
                            'desktop:mx-auto desktop:w-maxWidth',
                        )}
                    >
                        <FooterLegalLinks legalLinks={footerInformationResult.data.compliance} />
                        <CountriesLink className={'pb-1.5 desktopS:pb-0'} locale={locale} />
                    </div>
                </div>
                {featureFlipping.isAppVersionEnabled && envConfig.appVersion && (
                    <div className={'bg-background-brand text-center'}>
                        <GlowTextBodySmall
                            className={'py-1'}
                            text={t('version.label', { version: envConfig.appVersion })}
                            color={'secondary-inverse-fixed'}
                        />
                    </div>
                )}
            </UIFooterShell>
        )
    );
};
